import React, {useEffect, useMemo, useState, ReactNode} from 'react';
import {NavLink, useSearchParams} from 'react-router-dom';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {Input} from '~/components/Controls/Input';
import {SelectInput} from '~/components/Controls/Select';
import {Pagination} from '~/components/Pagination';
import {Progress} from '~/components/Progress';
import {BreadcrumbsResourcePage} from '~/components/ResourcePage';
import {Table} from '~/components/Table';
import {useAbility} from '~/lib/ability';
import {Intent} from '~/lib/intent';
import {useResource} from '~/lib/loader';
import {auditLinks} from '~/modules/Audit';
import {fetchAudit, IAuditFilter} from '~/modules/Audit/api';
import {
    EntityType,
    entityTypes,
    getEntityTypeLabel,
    getOperationTypeLabel,
    IAudit,
    OperationType,
    operationTypes
} from '~/modules/Audit/models/Audit';
import {clientsLinks} from '~/modules/Clients';
import {biddersLinks} from '~/modules/Bidders';
import {sourcesLinks} from '~/modules/Sources';
import {pmpLinks} from '~/modules/Pmp';
import {ClientsAbility} from '~/modules/Clients/ability';
import { JsonTreeViewer } from '~/components/Tree';
import style from './AuditView.module.scss';

const extractNumericId = (value: string | number): number | null => {
    if (value == null) {
        return null;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    const match = String(value).match(/(\d+)/);
    return match ? Number(match[1]) : null;
};

const extractBidderIdFromEntity = (entity: unknown): number | null => {
    if (!entity) {
        return null;
    }

    if (typeof entity === 'object') {
        const obj = entity as any;
        const bidderIdValue = obj.bidderId ?? obj.bidder?.id;

        if (bidderIdValue && typeof bidderIdValue === 'object' && 'value' in bidderIdValue) {
            const id = extractNumericId((bidderIdValue as any).value as any);
            if (id != null) {
                return id;
            }
        } else {
            const id = extractNumericId(bidderIdValue as any);
            if (id != null) {
                return id;
            }
        }

        return null;
    }

    if (typeof entity === 'string') {
        try {
            const parsed = JSON.parse(entity);
            return extractBidderIdFromEntity(parsed);
        } catch {
        }
    }

    return null;
};

const extractSellerIdFromEntity = (entity: unknown): number | null => {
    if (!entity) {
        return null;
    }

    if (typeof entity === 'object') {
        const sellerId = (entity as any).sellerId ?? (entity as any).seller?.id;
        return extractNumericId(sellerId as any);
    }

    if (typeof entity === 'string') {
        try {
            const parsed = JSON.parse(entity);
            const sellerId = (parsed as any).sellerId ?? (parsed as any).seller?.id;
            const parsedId = extractNumericId(sellerId as any);
            if (parsedId != null) {
                return parsedId;
            }
        } catch {
        }
    }

    return null;
};

const extractAgencyIdFromEntity = (entity: unknown): number | null => {
    if (!entity) {
        return null;
    }

    if (typeof entity === 'object') {
        const agencyId = (entity as any).agencyId ?? (entity as any).agency?.id;
        return extractNumericId(agencyId as any);
    }

    if (typeof entity === 'string') {
        try {
            const parsed = JSON.parse(entity);
            const agencyId = (parsed as any).agencyId ?? (parsed as any).agency?.id;
            const parsedId = extractNumericId(agencyId as any);
            if (parsedId != null) {
                return parsedId;
            }
        } catch {
        }
    }

    return null;
};

const extractPreTargetingConfigIdFromEntity = (entity: unknown): number | null => {
    if (!entity) {
        return null;
    }

    if (typeof entity === 'object') {
        const configId = (entity as any).preTargetingConfigId
            ?? (entity as any).config?.id
            ?? (entity as any).preTargetingConfig?.id;
        return extractNumericId(configId as any);
    }

    if (typeof entity === 'string') {
        try {
            const parsed = JSON.parse(entity);
            const configId = (parsed as any).preTargetingConfigId
                ?? (parsed as any).config?.id
                ?? (parsed as any).preTargetingConfig?.id;
            const parsedId = extractNumericId(configId as any);
            if (parsedId != null) {
                return parsedId;
            }
        } catch {
        }
    }

    return null;
};

const newTabProps = {target: '_blank' as const, rel: 'noopener noreferrer'};

const renderSellerLink = (sellerId: number | null, label: ReactNode) => {
    return sellerId != null
        ? <NavLink to={clientsLinks.seller(sellerId)} {...newTabProps}>{label}</NavLink>
        : label;
};

const renderBidderLink = (bidderId: number | null, label: ReactNode) => {
    return bidderId != null
        ? <NavLink to={biddersLinks.bidder(bidderId)} {...newTabProps}>{label}</NavLink>
        : label;
};

const renderAgencyLink = (agencyId: number | null, label: ReactNode) => {
    return agencyId != null
        ? <NavLink to={clientsLinks.agency(agencyId)} {...newTabProps}>{label}</NavLink>
        : label;
};

const renderEntityLabel = (audit: IAudit): ReactNode => audit.entityId;

const renderEntityLink = (audit: IAudit): ReactNode => {
    const numericId = extractNumericId(audit.entityId);
    const bidderId = extractBidderIdFromEntity(audit.entity);
    const sellerId = extractSellerIdFromEntity(audit.entity);
    const agencyId = extractAgencyIdFromEntity(audit.entity);
    const preTargetingConfigId = extractPreTargetingConfigIdFromEntity(audit.entity);

    const renderPreTargetingLink = () => {
        const targetConfigId = preTargetingConfigId ?? numericId;

        if (bidderId != null && targetConfigId != null) {
            return (
                <NavLink to={biddersLinks.bidderPreTargetingConfig(bidderId, targetConfigId)} {...newTabProps}>
                    {renderEntityLabel(audit)}
                </NavLink>
            );
        }

        if (bidderId != null) {
            return renderBidderLink(bidderId, renderEntityLabel(audit));
        }

        return renderEntityLabel(audit);
    };

    switch (audit.operationType) {
    case OperationType.DELETE:
        switch (audit.entityType) {
        case EntityType.ACCOUNT_INFO: {
            const targetSellerId = sellerId ?? numericId;
            return renderSellerLink(targetSellerId, renderEntityLabel(audit));
        }
        case EntityType.AGENCY:
            return renderEntityLabel(audit);
        case EntityType.APP_ADS:
            return renderEntityLabel(audit);
        case EntityType.BIDDER:
            return renderEntityLabel(audit);
        case EntityType.BIDDER_GROUP:
            return renderEntityLabel(audit);
        case EntityType.EXCHANGE_CONFIG:
            return <NavLink to={clientsLinks.sellers()} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>;
        case EntityType.PAYMENT_DETAILS: {
            const targetSellerId = sellerId ?? numericId;
            return renderSellerLink(targetSellerId, renderEntityLabel(audit));
        }
        case EntityType.PRETARGETING_CONFIG:
        case EntityType.PRETARGETING_RULES:
        case EntityType.PRETARGETING_VARIABLE:
            return renderEntityLabel(audit);
        case EntityType.SELLER:
            return renderEntityLabel(audit);
        case EntityType.SELLER_BLOCKING_SETTINGS: {
            const targetSellerId = sellerId;
            return renderSellerLink(targetSellerId, renderEntityLabel(audit));
        }
        case EntityType.SOURCE: {
            const targetSellerId = sellerId;
            return renderSellerLink(targetSellerId, renderEntityLabel(audit));
        }
        case EntityType.SUPPLY_PACKAGE:
            return renderEntityLabel(audit);
        case EntityType.DEAL:
            return renderEntityLabel(audit);
        default:
            return renderEntityLabel(audit);
        }
    case OperationType.CREATE:
        switch (audit.entityType) {
        case EntityType.ACCOUNT_INFO: {
            const targetSellerId = sellerId ?? numericId;
            return renderSellerLink(targetSellerId, renderEntityLabel(audit));
        }
        case EntityType.AGENCY:
            return numericId != null
                ? <NavLink to={clientsLinks.agency(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.APP_ADS:
            return renderEntityLabel(audit);
        case EntityType.BIDDER:
            return numericId != null
                ? <NavLink to={biddersLinks.bidder(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.BIDDER_GROUP:
            return numericId != null
                ? <NavLink to={biddersLinks.bidderGroup(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.EXCHANGE_CONFIG:
            return renderEntityLabel(audit);
        case EntityType.PAYMENT_DETAILS:
            return renderEntityLabel(audit);
        case EntityType.PRETARGETING_CONFIG:
        case EntityType.PRETARGETING_RULES:
        case EntityType.PRETARGETING_VARIABLE:
            return renderPreTargetingLink();
        case EntityType.SELLER:
            return numericId != null
                ? <NavLink to={clientsLinks.seller(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.SELLER_BLOCKING_SETTINGS: {
            const targetSellerId = sellerId ?? numericId;
            return renderSellerLink(targetSellerId, renderEntityLabel(audit));
        }
        case EntityType.SOURCE:
            return numericId != null
                ? <NavLink to={sourcesLinks.source(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.SUPPLY_PACKAGE:
            return numericId != null
                ? <NavLink to={pmpLinks.supplyPackage(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.DEAL:
            return <NavLink to={pmpLinks.deals()} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>;
        default:
            return renderEntityLabel(audit);
        }
    case OperationType.UPDATE:
        switch (audit.entityType) {
        case EntityType.ACCOUNT_INFO: {
            const targetSellerId = sellerId ?? numericId;
            return renderSellerLink(targetSellerId, renderEntityLabel(audit));
        }
        case EntityType.AGENCY:
            return numericId != null
                ? <NavLink to={clientsLinks.agency(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.APP_ADS:
            return renderEntityLabel(audit);
        case EntityType.BIDDER:
            return numericId != null
                ? <NavLink to={biddersLinks.bidder(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.BIDDER_GROUP:
            return numericId != null
                ? <NavLink to={biddersLinks.bidderGroup(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.EXCHANGE_CONFIG:
            return renderEntityLabel(audit);
        case EntityType.PAYMENT_DETAILS: {
            const targetSellerId = sellerId ?? numericId;
            return renderSellerLink(targetSellerId, renderEntityLabel(audit));
        }
        case EntityType.PRETARGETING_CONFIG:
        case EntityType.PRETARGETING_RULES:
        case EntityType.PRETARGETING_VARIABLE:
            return renderPreTargetingLink();
        case EntityType.SELLER:
            return numericId != null
                ? <NavLink to={clientsLinks.seller(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.SELLER_BLOCKING_SETTINGS: {
            const targetSellerId = sellerId ?? numericId;
            return renderSellerLink(targetSellerId, renderEntityLabel(audit));
        }
        case EntityType.SOURCE:
            return numericId != null
                ? <NavLink to={sourcesLinks.source(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.SUPPLY_PACKAGE:
            return numericId != null
                ? <NavLink to={pmpLinks.supplyPackage(numericId)} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>
                : renderEntityLabel(audit);
        case EntityType.DEAL:
            return <NavLink to={pmpLinks.deals()} {...newTabProps}>{renderEntityLabel(audit)}</NavLink>;
        default:
            return renderEntityLabel(audit);
        }
    default:
        return renderEntityLabel(audit);
    }
};

const parseSearchParams = (searchParams: URLSearchParams): IAuditFilter => ({
    entityType: (searchParams.get('entityType') as EntityType) || null,
    entityId: searchParams.get('entityId') || '',
    operationType: (searchParams.get('operationType') as OperationType) || null,
    userId: searchParams.get('userId') || '',
});

const syncFiltersToUrl = (filters: IAuditFilter, setSearchParams: (params: URLSearchParams) => void) => {
    const params = new URLSearchParams();
    if (filters.entityType) params.set('entityType', filters.entityType);
    if (filters.entityId) params.set('entityId', filters.entityId);
    if (filters.operationType) params.set('operationType', filters.operationType);
    if (filters.userId) params.set('userId', filters.userId);
    setSearchParams(params);
};

export const AuditView = () => {
    const ability = useAbility(ClientsAbility);
    const [searchParams, setSearchParams] = useSearchParams();

    const [submitted, setSubmitted] = useState(0);

    const [filters, setFilters] = useState<IAuditFilter>(() => parseSearchParams(searchParams));

    const [paginator, setPaginator] = useState<{ page: number; pageSize: number }>({page: 0, pageSize: null});

    const result = useResource(() => fetchAudit(filters, {
        pageSize: paginator.pageSize,
        pageNo: paginator.page
    }), {items: [], count: 0, page: 1, pageSize: 0});

    useEffect(() => {
        result.fetch();
    }, [paginator]);

    const hasUrlFilters = useMemo(() => {
        const initial = parseSearchParams(searchParams);
        return !!(initial.entityType || initial.entityId || initial.operationType || initial.userId);
    }, []);

    useEffect(() => {
        if (hasUrlFilters) {
            setSubmitted(1);
            result.fetch();
        }
    }, []);

    const submitHandler = (event) => {
        setSubmitted(1);
        event.preventDefault();
        syncFiltersToUrl(filters, setSearchParams);
        result.fetch();
    };

    return <Breadcrumb breadcrumb={{
        to: auditLinks.audit(),
        title: 'Audit'
    }}>
        <BreadcrumbsResourcePage title='Audit'>
            <div className="layout-content-box">
                <form className={style.auditFilter + (submitted ? ' ' + style.isSubmitted  : '')} onSubmit={submitHandler}>
                    <SelectInput items={entityTypes} value={filters.entityType}
                        onChange={entityType => setFilters({...filters, entityType})}
                        placeholder='Type' disabled={result.isPending} clearable
                        className={style.entityTypeSelect}/>
                    <Input
                        value={filters.entityId}
                        onChange={entityId => setFilters({...filters, entityId})}
                        placeholder='Entity ID'
                        disabled={result.isPending}
                        className={style.entityIdInput}
                        rightIcon={<i className='fa fa-search input-search-icon' />}
                    />
                    <SelectInput items={operationTypes} value={filters.operationType}
                        onChange={operationType => setFilters({...filters, operationType})}
                        placeholder='Operation' disabled={result.isPending} clearable
                        className={style.operationTypeSelect}/>
                    <Input
                        value={filters.userId}
                        onChange={userId => setFilters({...filters, userId})}
                        placeholder='User ID'
                        disabled={result.isPending}
                        className={style.userIdInput}
                        rightIcon={<i className='fa fa-search input-search-icon' />}
                    />
                    <Button onClick={() => setSubmitted(1)} type={'submit'} rightIcon={<i className='fa fa-search'/>} intent={Intent.PRIMARY}
                        disabled={result.isPending}
                        className={style.searchButton}>Search</Button>
                </form>
                <Table header
                    tableAttrs={{
                        className: style.auditTable
                    }}
                    columns={[{
                    header: 'Time (UTC)',
                    field: 'timestamp',
                    cell: audit => {
                        const d = new Date(audit.timestamp);
                        const pad = (n: number) => String(n).padStart(2, '0');
                        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
                    }
                }, {
                    header: 'Type',
                    field: 'entityType',
                    cell: audit => getEntityTypeLabel(audit.entityType)
                }, {
                    header: 'Entity ID',
                    field: 'entityId',
                    cell: (audit: IAudit) => renderEntityLink(audit)
                }, {
                    header: 'Operation',
                    field: 'operationType',
                    cell: audit => getOperationTypeLabel(audit.operationType)
                }, {
                    header: 'User ID',
                    cell: audit => (ability.isSeller() || ability.isSelfServe()) ? audit.userId : <NavLink
                        to={clientsLinks.users(audit.userId)} target="_blank" rel="noopener noreferrer">{audit.userId}</NavLink>
                }, {
                    header: 'Preview',
                    cell: audit => (
                        <JsonTreeViewer
                            data={audit.entity}
                            showCopy
                        />
                    ),
                    cellAttrs: {
                        className: style.entityCell
                    }
                }]}
                records={result.resource.items}
                placeholder={result.isPending && <Progress/>}/>
            {!result.isPending &&
            <Pagination padding={4} offset={result.resource.pageSize * result.resource.page} limit={result.resource.pageSize}
                total={result.resource.count} onChange={(page, perPage) => {
                    setPaginator({page, pageSize: perPage});
                }}/>}
        </div>
        </BreadcrumbsResourcePage>
    </Breadcrumb>;
};
